import type { SpecType } from '@local/lib';

import { Button, Checkbox, Divider, Group, Popover, Stack, Text } from '@mantine/core';
import { ListFilter } from 'lucide-react';

import type { SpecDefinition } from '../qualityFormulaTypes';

interface QualityTypeFilterProps {
  onChange: (specTypes: SpecType[]) => void;
  specTypes: SpecDefinition[];
  value: SpecType[];
}

// `value` is the explicit list of currently visible spec types (all spec types by default) — a fixed-size
// button + checklist popover avoids the unbounded pill growth a MultiSelect produces once several
// options are picked.
export const QualityTypeFilter: React.FC<QualityTypeFilterProps> = ({ onChange, specTypes, value }) => {
  const isAllVisible = value.length === specTypes.length;

  const toggleKey = (key: SpecType) => {
    onChange(value.includes(key) ? value.filter(visibleKey => visibleKey !== key) : [...value, key]);
  };

  return (
    <Popover position="bottom-end" shadow="md" withArrow withinPortal={false}>
      <Popover.Target>
        <Button leftSection={<ListFilter size={14} />} size="xs" variant={isAllVisible ? 'outline' : 'light'}>
          {`Select (${value.length})`}
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap={6} w={220}>
          <Group justify="space-between">
            <Text c="dimmed" fw={700} fz={11} lts={0.3} tt="uppercase">
              Show quality types
            </Text>
            <Group gap={4}>
              <Button
                onClick={() => {
                  onChange(specTypes.map(spec => spec.key));
                }}
                size="compact-xs"
                variant="subtle"
              >
                All
              </Button>
              <Button
                onClick={() => {
                  onChange([]);
                }}
                size="compact-xs"
                variant="subtle"
              >
                Clear
              </Button>
            </Group>
          </Group>
          <Divider />
          <Stack gap={4}>
            {specTypes.map(spec => (
              <Checkbox
                checked={value.includes(spec.key)}
                key={spec.key}
                label={spec.label}
                onChange={() => {
                  toggleKey(spec.key);
                }}
                styles={{ input: { cursor: 'pointer' }, label: { cursor: 'pointer' } }}
              />
            ))}
          </Stack>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};
